import { Grid, GridItem, TabContent } from "@patternfly/react-core";
import { type FC, useEffect, useMemo, useState } from "react";
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import Spinner from "../general/Spinner/Spinner";
import ProductDetailHeader from "./ProductDetailHeader/ProductDetailHeader";
import "./ProductDetailsWrapper.scss";
import { ErrorPopup } from "@rhc-shared-components/alert-component";
import { RHCErrorPage } from "@rhc-shared-components/error-page";
import { useQueryClient } from "@tanstack/react-query";
import { map } from "lodash";
import { useParams } from "react-router";
import { getProductDetailsRouteList, type ProductRouteParams, TabIDs } from "../../constants/routerconstants";
import { useAIRateLimit } from "../../queries/connectAI";
import {
	checklistStatusQueryKey,
	productDetailQueryKey,
	productScoreQueryKey,
	useChecklistStatus,
	useCompanyDetails,
	useProductDetail,
	useProductScore,
	useRedhatProductVersions,
	useSuperGraphProductListing,
} from "../../queries/productDetails";
import { getOrgID } from "../../utils/fetch";
import { ProductComponents } from "../ProductComponents/ProductComponents";
import ProductInfo from "../ProductInfo/ProductInfo";
import ProductOverview from "../ProductOverview/ProductOverview";
import ProductSupport from "../ProductSupportTab/ProductSupport";
import ProductValidationsAndCertifications from "../ProductValidationsAndCertifications/ProductValidationsAndCertifications";

interface LocationState {
	fromProductList?: boolean;
}

const ProductTabContent: FC<{ id: string }> = ({ id }) => {
	return (
		<TabContent id={id}>
			{(() => {
				switch (id) {
					case TabIDs.Overview:
						return <ProductOverview />;
					case TabIDs.ProductInformation:
						return <ProductInfo />;
					case TabIDs.Components:
						return <ProductComponents />;
					case TabIDs.Support:
						return <ProductSupport />;
					case TabIDs.CertificationsAndValidations:
						return <ProductValidationsAndCertifications />;
					default:
						return <RHCErrorPage errorStatus={404} />;
				}
			})()}
		</TabContent>
	);
};

const ProductDetailsWrapper: FC = () => {
	const match = useRouteMatch<ProductRouteParams>();
	const location = useLocation<LocationState>();
	const history = useHistory();
	const [responseError, setError] = useState<any>(null);
	const [permissionError, setPermissionError] = useState(false);
	const params = useParams<ProductRouteParams>();
	const queryClient = useQueryClient();
	const { error: redhatVersionsError } = useRedhatProductVersions();
	const {
		isLoading: productDetailsLoading,
		data: productDetail,
		error: productDetailError,
	} = useProductDetail(params.productId);

	//Todo: Add the isloading and error handlers back after https://redhat.atlassian.net/browse/ISV-7479 is completed
	useSuperGraphProductListing(params?.productId);

	useEffect(() => {
		if (redhatVersionsError) {
			ErrorPopup(setError, "Something went wrong while fetching Red Hat product versions");
		}
	}, [redhatVersionsError]);

	useEffect(() => {
		if (productDetailError) {
			setError(productDetailError);
		}
	}, [productDetailError]);

	useEffect(() => {
		if (productDetail) {
			if (!(productDetail?.org_id === Number(getOrgID()) && !productDetail?.deleted)) {
				setPermissionError(true);
			}
		}
	}, [productDetail]);

	const routes = useMemo(
		() => getProductDetailsRouteList(params?.productId, productDetail),
		[params?.productId, productDetail],
	);
	const { error: productScoreError } = useProductScore(params.productId);

	useEffect(() => {
		if (productScoreError) {
			setError(productScoreError);
		}
	}, [productScoreError]);

	const { isLoading: companyDetailsLoading, error: companyDetailsError } = useCompanyDetails();
	const { isLoading: aiRateLimitLoading } = useAIRateLimit();

	useEffect(() => {
		if (companyDetailsError) {
			setError(companyDetailsError);
		}
	}, [companyDetailsError]);

	// This will be re-used later in the app on checklist and header
	const { error: checklistError } = useChecklistStatus(params.productId);

	useEffect(() => {
		if (checklistError) {
			setError(checklistError);
		}
	}, [checklistError]);

	// Background refetch when navigating from product list
	useEffect(() => {
		if (location.state?.fromProductList) {
			// Invalidate queries to trigger background refetch
			void queryClient.invalidateQueries({ queryKey: productDetailQueryKey(params.productId) });
			void queryClient.invalidateQueries({ queryKey: productScoreQueryKey(params.productId) });
			void queryClient.invalidateQueries({ queryKey: checklistStatusQueryKey(params.productId) });
			// Clear the state to prevent refetching on tab navigation
			history.replace(location.pathname, {});
		}
	}, [location.state?.fromProductList, params.productId, queryClient, history, location.pathname]);

	const isLoading = productDetailsLoading || companyDetailsLoading || aiRateLimitLoading;
	if (isLoading) {
		return <Spinner />;
	}
	if (permissionError) {
		return <RHCErrorPage errorStatus={404} />;
	}
	if (responseError) {
		return <RHCErrorPage errorStatus={responseError?.status} />;
	}

	return (
		<>
			<ProductDetailHeader />
			<Grid className="product-detail-main-grid">
				<GridItem span={10} offset={1}>
					<div className="product-detail-container">
						<Switch>
							<Redirect exact from={match.url} to={`${match.url}/overview`} />
							{map(routes, (nav, index) => {
								return (
									<Route
										key={index}
										path={nav.routeMatch}
										exact={nav.exact}
										render={() => <ProductTabContent id={nav.id} />}
									/>
								);
							})}
							<Route render={() => <RHCErrorPage errorStatus={404} />} />
						</Switch>
					</div>
				</GridItem>
			</Grid>
		</>
	);
};

export default ProductDetailsWrapper;
